import { useEffect, useMemo, useState } from "react";
import CoursableIcons from "../../../../../utils/CoursableIcons";
import useDidScroll from "../../../../../utils/Hooks/useDidScroll";
import LoadingIndicator from "../../../../elements/LoadingIndicator";
import Dropdown from "../../../../elements/DropdownMenu/Dropdown";
import { ContextMenu, ContextMenuButton, ContextMenuDivider, ContextMenuLoadingLabel } from "../../../../elements/DropdownMenu/ContextMenu";
import { ContextMenuDeleteConfirmation } from "../../../../elements/DropdownMenu/ContextMenuConfirmation";
import { MarkdownWithAnnotations } from "../../ProjectAnnotations";
import { GenerationBlock, useGenerationBlock } from "../GenerationBlock";
import { DoneButton, formattedDateGenerated, ShareProjectDerivative, useWorkspaceStates } from "../Shared";
import { useLocation } from "react-router-dom";
import SSManager from "../../../../../utils/SessionStorageManager";
import InputField from "../../../../elements/InputField";
import InputBox from "../../../../elements/InputBox";
import { useProjectManager } from "../../Hooks/useProjectManager";
import { useNotifications } from "../../../../../utils/NotificationsContext";
import { cn } from "../../../../../utils/UtilityMethods";

interface ProjectSummariesProps {
  materialsViewerExpanded: boolean;
}

const ProjectSummaries = ({ materialsViewerExpanded }: ProjectSummariesProps) => {
  const state = useLocation().state as { isNewProject: boolean } | undefined;
  const { project, Summaries, Files } = useProjectManager();
  const { prompt, setPrompt, selectedFiles, setSelectedFiles } = useGenerationBlock(Files.files);

  const [selectedSummaryIndex, setSelectedSummaryIndex] = useState<number | null>(Summaries.summaries.length > 0 ? 0 : null);
  const selectedSummary = selectedSummaryIndex !== null ? Summaries.summaries[selectedSummaryIndex] : null;

  const [summaryName, setSummaryName] = useState<string | null>(selectedSummary?.name ?? null);
  const [summaryContent, setSummaryContent] = useState<string | null>(selectedSummary?.content ?? null);
  const { scrollRef, didScroll } = useDidScroll<HTMLDivElement>();
  const { ShareDropdown, SharePopup } = ShareProjectDerivative({
    type: "summary",
    derivative: selectedSummary ?? undefined,
    projectID: project?.id,
    setPublicID: (publicID) => (selectedSummary ? Summaries.SetPublicID(selectedSummary.id, publicID) : undefined),
  });

  const dateGenerated = useMemo(() => {
    if (!selectedSummary) return "";
    return formattedDateGenerated(selectedSummary.dateCreated);
  }, [selectedSummary]);

  const [shownSummaryNameIndex, setShownSummaryNameIndex] = useState<number>(selectedSummary?.name.length ?? 0);

  const { renaming, setRenaming, editing, setEditing, saving, setSaving, deleting, setDeleting } = useWorkspaceStates();

  const [showingDeleteConfirmation, setShowingDeleteConfirmation] = useState<boolean>(false);

  const { sendError } = useNotifications();

  async function ClickGenerateSummary() {
    if (!project) return;

    try {
      const summary = await Summaries.Generate(selectedFiles, prompt, selectedSummary?.id);
      setSelectedSummaryIndex(0);
      setSummaryName(summary.name);
      StartTypingSummaryName(summary.name.length);
    } catch (error: any) {
      sendError("Summary generation failed.", "Could not generate summary from your materials, please try again.");
      Summaries.SetIsGenerating(false);
    }
  }

  async function ClickSaveSummaryName() {
    if (!selectedSummary) return;

    setSaving(true);
    try {
      await Summaries.SaveName(selectedSummary.id, summaryName ?? "");
      setRenaming(false);
    } catch (error) {
      sendError();
    }
    setSaving(false);
  }

  async function ClickSaveSummaryContent() {
    if (!selectedSummary) return;

    setSaving(true);
    try {
      await Summaries.SaveContent(selectedSummary.id, summaryContent ?? "");
      setEditing(false);
    } catch (error) {
      sendError();
    }
    setSaving(false);
  }

  async function ClickDeleteSummary() {
    if (!selectedSummary) return;

    setDeleting(true);
    try {
      await Summaries.Delete(selectedSummary.id);
      setSelectedSummaryIndex(null);
    } catch (error) {
      sendError();
    }
    setDeleting(false);
  }

  function StartTypingSummaryName(length: number, index: number = 0) {
    if (index !== 0 && index > length) return;
    setShownSummaryNameIndex(index);

    setTimeout(() => {
      StartTypingSummaryName(length, index + 1);
    }, 50);
  }

  useEffect(() => {
    setSelectedSummaryIndex(Summaries.summaries.length > 0 ? 0 : null);
  }, [Summaries.summaries]);

  useEffect(() => {
    setSummaryName(selectedSummary?.name ?? null);
    setSummaryContent(selectedSummary?.content ?? null);
  }, [selectedSummary]);

  useEffect(() => {
    if (!project) return;

    const autoGeneratedOnce = SSManager.get<boolean>(`${project.id}:auto-generated-summary`);
    let timer: NodeJS.Timeout | undefined;

    if (state?.isNewProject && !autoGeneratedOnce) {
      timer = setTimeout(() => {
        SSManager.set<boolean>(`${project.id}:auto-generated-summary`, true);
        ClickGenerateSummary();
      }, 1000);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  return (
    <div className={`w-full flex flex-col items-start justify-start gap-4 pt-6 overflow-auto h-full`}>
      <div className="w-full text-sm text-systemGray-500 px-4">Generate beautiful document summaries to get a better glance at what you are working with.</div>
      <GenerationBlock
        prompt={prompt}
        generating={project?.isGeneratingSummary ?? false}
        empty={!selectedSummary}
        files={Files.files}
        selectedFiles={selectedFiles}
        setPrompt={setPrompt}
        setSelectedFiles={setSelectedFiles}
        ClickGenerate={ClickGenerateSummary}
        promptPlaceholder="Ask for anything specific in your summary"
        className="px-4"
      />
      <div className="border-b mt-4 w-full" />
      {selectedSummary && summaryContent !== "" ? (
        <>
          <div className={`w-full flex justify-between items-start gap-4 overflow-x-clip px-4 shrink-0 z-1 ${saving && "pointer-events-none opacity-70"} @container`}>
            {renaming ? (
              <>
                <InputField className="w-full" value={summaryName ?? ""} onValueChange={setSummaryName} />
                <DoneButton onClick={ClickSaveSummaryName} saving={saving} />
              </>
            ) : (
              <>
                <div className="w-full flex flex-col items-start justify-start relative my-2">
                  {selectedSummary.publicID && <div className="absolute bottom-full left-0 text-xs text-brandBlue-300 flex items-center justify-start gap-1">{CoursableIcons.PersonsFill("text-base -mt-0.5")} Shared</div>}
                  <div className="text-brand-500 text-sm md:text-base">{summaryName?.slice(0, shownSummaryNameIndex)}</div>
                  <div className="text-systemGray-400 text-xs font-light">{dateGenerated}</div>
                </div>
                {editing ? (
                  <DoneButton onClick={ClickSaveSummaryContent} saving={saving} />
                ) : (
                  <div className="flex flex-col items-end @md:flex-row gap-2 mt-2">
                    {ShareDropdown}
                    <div className="relative">
                      <Dropdown
                        label={
                          <>
                            <span className="hidden @md:inline">More</span>
                            {CoursableIcons.Ellipsis()}
                          </>
                        }
                        chevron={false}
                        className="@md:w-auto h-9 w-9"
                      >
                        <ContextMenuButton label="Rename" icon={CoursableIcons.Edit()} onClick={() => setRenaming(true)} />
                        <ContextMenuButton label="Edit" icon={CoursableIcons.Edit()} onClick={() => setEditing(true)} />
                        <ContextMenuButton
                          label="Export as PDF"
                          icon={CoursableIcons.Share()}
                          onClick={() => {
                            window.open(`/app/projects/${project?.id}/summary/${selectedSummary.id}/pdf`, "_blank");
                          }}
                        />
                        <ContextMenuDivider />
                        <ContextMenuButton label="Delete" icon={CoursableIcons.Delete()} onClick={() => setShowingDeleteConfirmation(true)} destructive />
                      </Dropdown>
                      <ContextMenuDeleteConfirmation label="Are you sure you want to delete this summary?" width={200} isOpen={showingDeleteConfirmation} setIsOpen={setShowingDeleteConfirmation} onDelete={ClickDeleteSummary} />
                      <ContextMenu autoClose={false} isOpen={deleting} setIsOpen={setDeleting}>
                        <ContextMenuLoadingLabel label="Deleting" />
                      </ContextMenu>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          {editing ? (
            <>
              <InputBox className="w-full resize-none h-full" containerClassName={`w-full h-full px-4`} value={summaryContent ?? ""} onValueChange={setSummaryContent} />
              <div className={`text-sm text-systemGray-500 px-6 ${materialsViewerExpanded ? "pb-6" : "pb-16 "}`}>
                Summaries are styled with markdown. Learn more about it{" "}
                <a className="underline text-brand-400 hover:text-brand-500 active:text-brand-400" href="https://www.markdownguide.org/basic-syntax/" target="_blank">
                  here
                </a>
                .
              </div>
            </>
          ) : (
            <>
              <div ref={scrollRef} className={cn(`w-full flex items-start gap-4 md:overflow-auto md:h-full px-4 relative`, didScroll && "border-t", materialsViewerExpanded ? "mb-0" : "mb-10")}>
                <MarkdownWithAnnotations annotations={selectedSummary.annotations} className="!prose-sm md:!prose-base">
                  {summaryContent}
                </MarkdownWithAnnotations>
              </div>
            </>
          )}
        </>
      ) : (
        <div className="flex items-center justify-start w-full gap-1 opacity-60 px-4">
          {!!project?.isGeneratingSummary ? (
            <>
              <span className="text-lg">🔍</span> Generating summary <LoadingIndicator className="inline h-4 stroke-white mt-2 -ml-1 fill-systemGray-800" type="dots" />
            </>
          ) : (
            <>
              <span className="text-lg">📖</span> Your generated summary will appear here
            </>
          )}
        </div>
      )}
      {SharePopup}
    </div>
  );
};

export default ProjectSummaries;
